// src/components/products/ProductList/index.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Package,
    Search,
    Filter,
    Eye,
    Edit,
    Trash2,
    Clock,
    RefreshCw,
    Package as PackageIcon,
    ClipboardCheck,
    Plus,
    Archive,
    MoreVertical,
    Undo2
} from 'lucide-react';
import { formatPrice } from '../../../utils/utilityFunctions';
import { getProductStatusBadge } from '../../../utils/statusBadges';
import StockCell from '../StockCell';
import type { Product } from '../../../types';
import { getProductImageUrl } from '../../../utils/productUtilities';
import toast from '../../../utils/toast';
import { useVendor } from '../../../hooks/useVendor';
import StatusBadge from '../../common/StatusBadge';

type ProductAction = 'view' | 'edit' | 'delete' | 'review' | 'stock' | 'submit' | 'resubmit' | 'archive' | 'rollback';

interface ProductListProps {
    products: Product[];
    loading: boolean;
    onRefresh?: () => void;
    showVendorColumn?: boolean;
    showCategoriesColumn?: boolean;
    actions: ProductAction[];
    onRowClick?: (product: Product) => void;
    onAction?: (action: ProductAction, product: Product) => void;
    isVendor?: boolean;
    vendorId?: string;
    title?: string;
    subtitle?: string;
    emptyMessage?: string;
    rowClickMode?: 'edit' | 'review' | 'view' | 'none';
    isAdmin?: boolean;
    showAddButton?: boolean;
    onAddProduct?: () => void;
    addButtonText?: string;
    onProductEdit?: (product: Product) => void;
    onProductView?: (product: Product) => void;
    onProductDelete?: (product: Product) => void;
    onProductReview?: (product: Product) => void;
    onProductSubmit?: (product: Product) => void;
    onProductResubmit?: (product: Product) => void;
    onProductArchive?: (product: Product) => void;
    onProductUnarchive?: (product: Product) => void;
    onStockAdjust?: (product: Product) => void;
    onSearchChange: (search: string) => void;
    onRollbackChanges?: (product: Product) => void;
    showActiveColumn?: boolean;
    pagination?: {
        page: number;
        total: number;
        pages: number;
        pageSize: number;
    };
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    onFilterChange?: (status: string) => void;
    statusFilter?: string;
    onStatusFilterChange?: (filter: string) => void;
    showStoreColumn?: boolean;
}

const ProductList: React.FC<ProductListProps> = ({
    products,
    loading,
    onRefresh,
    showVendorColumn = true,
    showCategoriesColumn = true,
    actions = ['view'],
    onRowClick,
    onAction,
    vendorId,
    title = 'Products',
    subtitle,
    emptyMessage = 'No products found',
    rowClickMode = 'view',
    isAdmin = false,
    showAddButton = false,
    onAddProduct,
    addButtonText = 'Add Product',
    onProductEdit,
    onProductView,
    onProductDelete,
    onProductReview,
    onProductSubmit,
    onProductResubmit,
    onProductArchive,
    onProductUnarchive,
    onStockAdjust,
    onRollbackChanges,
    showActiveColumn = true,
    pagination,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onFilterChange,
    statusFilter,
    showStoreColumn = false,
}) => {
    const navigate = useNavigate();
    const { vendor } = useVendor();
    // const showStoreColumn = isAdmin || (!isAdmin && hasMultipleStores);
    const [localSearch, setLocalSearch] = useState('');
    const [openDropdown, setOpenDropdown] = useState<number | null>(null);
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);
    // const [statusFilter, setStatusFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom');
    const tooltipRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = () => setOpenDropdown(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const statusOptions = [
        { value: 'all', label: 'All' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending_review', label: 'Pending Review' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'changes_requested', label: 'Changes Requested' },
        { value: 'archived', label: 'Archived' },
    ];

    const toggleDropdown = (productId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenDropdown(openDropdown === productId ? null : productId);
    };

    const handleMouseEnter = (productId: string, e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Check if there's more space below or above
        setTooltipPosition(spaceBelow > 200 ? 'bottom' : 'top');
        setActiveTooltip(productId);
    };

    const handleMouseLeave = () => {
        setActiveTooltip(null);
    };

    // Frontend filter (search on current page)
    const filteredProducts = products.filter(product => {
        const matchesSearch = product.title.toLowerCase().includes(localSearch.toLowerCase()) ||
            product.sku.toLowerCase().includes(localSearch.toLowerCase());
        return matchesSearch;
    });

    const pendingCount = products.filter(p => p.status === 'pending_review').length;

    const handleAction = (action: string, product: Product, e: React.MouseEvent) => {
        e.stopPropagation();
        switch (action) {
            case 'view':
                onProductView?.(product);
                break;
            case 'edit':
                if (!product.can_edit) {
                    toast.warning(product.edit_disabled_reason || 'This product cannot be edited');
                    return;
                }
                onProductEdit?.(product);
                break;
            case 'delete':
                if (!product.can_delete) {
                    toast.warning(product.delete_disabled_reason || 'Cannot delete this product');
                    return;
                }
                onProductDelete?.(product);
                break;
            case 'review':
                if (product.status !== 'pending_review') {
                    toast.info('This product is not pending review');
                    return;
                }
                onProductReview?.(product);
                break;
            case 'stock':
                onStockAdjust?.(product);
                break;
            case 'submit':
                onProductSubmit?.(product);
                break;
            case 'resubmit':
                onProductResubmit?.(product);
                break;
            case 'archive':
                if (!product.can_archive) {
                    toast.warning(product.archive_disabled_reason ?? '');
                    return;
                }
                onProductArchive?.(product);
                break;
            case 'unarchive':
                onProductUnarchive?.(product);
                break;
            case 'rollback':
                if (!product.can_rollback) {
                    toast.warning(product.rollback_disabled_reason || 'This product cannot be rolled back');
                    return;
                }
                onRollbackChanges?.(product);
                break;
            default:
                break;
        }
    };

    const handleSearchChange = (value: string) => {
        setLocalSearch(value);

        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        searchTimeout.current = setTimeout(() => {
            onSearchChange(value);
        }, 500);
    };


    const handleRowClick = (product: Product) => {
        if (onRowClick) {
            onRowClick(product);
            return;
        }

        // Default row click behavior based on mode
        switch (rowClickMode) {
            case 'edit':
                // Open in edit mode
                onAction?.('edit', product);
                break;
            case 'review':
                // Open in review mode (staff only)
                // if (isAdmin) {
                onAction?.('review', product);
                // } else {
                //     onAction?.('view', product);
                // }
                break;
            case 'view':
                navigate(`/dashboard/products/${product.id}`);
                break;
            case 'none':
                break;
            default:
                break;
        }
    };

    const handleFilterChange = (filter: string) => {
        onFilterChange?.(filter); // Notify parent
        // Close the dropdown
        // setShowFilters(false);
    };

    // After fixing pagination:
    // 1. Ensure submit works
    // 2. Ensure edit works
    // 3. Double check view works
    // 4. Double check review works
    // 5. Ensure approve works with email
    // 6. Ensure reject works with email
    // 7. Add form validation for product form modal
    // 8. Allow the search field to also be used to search for vendor names and skus
    // 9. Allow categories when searching, either in searchbar or by dropdown, whichever is better
    const renderPagination = () => {
        if (!pagination || pagination.pages <= 1) return null;

        const { page, total, pages, pageSize } = pagination;
        const startItem = (page - 1) * pageSize + 1;
        const endItem = Math.min(page * pageSize, total);

        return (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                    Showing <span className="font-medium">{startItem}</span> to{' '}
                    <span className="font-medium">{endItem}</span> of{' '}
                    <span className="font-medium">{total}</span> products
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPageChange?.(page - 1)}
                        disabled={page === 1}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                        let pageNum;
                        if (pages <= 5) {
                            pageNum = i + 1;
                        } else if (page <= 3) {
                            pageNum = i + 1;
                        } else if (page >= pages - 2) {
                            pageNum = pages - 4 + i;
                        } else {
                            pageNum = page - 2 + i;
                        }

                        return (
                            <button
                                key={pageNum}
                                onClick={() => onPageChange?.(pageNum)}
                                className={`px-3 py-1 rounded-lg text-sm ${page === pageNum
                                    ? 'bg-blue-600 text-white'
                                    : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                                    }`}
                            >
                                {pageNum}
                            </button>
                        );
                    })}
                    <button
                        onClick={() => onPageChange?.(page + 1)}
                        disabled={page === pages}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
                    {pendingCount > 0 && (
                        <p className="text-sm text-yellow-600 flex items-center gap-1 mt-1">
                            <Clock className="h-3.5 w-3.5" />
                            {pendingCount} product{pendingCount !== 1 ? 's' : ''} pending review
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {showAddButton && (
                        <button
                            onClick={onAddProduct}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus size={18} className="mr-1.5" />
                            {addButtonText || 'Add Product'}
                        </button>
                    )}
                    {onRefresh && (
                        <button
                            onClick={onRefresh}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className="h-5 w-5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={localSearch}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    {loading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                        </div>
                    )}
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    <Filter className="h-4 w-4" />
                    {statusFilter != undefined && statusFilter !== 'all' && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    {statusOptions.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => {
                                handleFilterChange(opt.value);
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === opt.value
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Products Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="text-center py-12">
                            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">{emptyMessage}</p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {showActiveColumn && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Active
                                        </th>
                                    )}
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                    {showVendorColumn && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                                    )}
                                    {showStoreColumn && (
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                                            Store
                                        </th>
                                    )}
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                    {showCategoriesColumn && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categories</th>
                                    )}
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review Status</th>
                                    {/* {showStoreColumn && (
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Store</th>
                                    )} */}
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>

                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredProducts.map((product) => {
                                    const status = getProductStatusBadge(product.status);
                                    const StatusIcon = status.icon;

                                    return (
                                        <tr
                                            key={product.id}
                                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                                            onClick={() => handleRowClick(product)}
                                        >
                                            <td className="px-6 py-4">
                                                <StatusBadge isActive={product.is_active} size="sm" />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="h-10 w-10 flex-shrink-0">
                                                        {/* Get primary image first, fallback to first image */}
                                                        {(() => {
                                                            const primaryImage = product.images?.find(img => img.is_primary);
                                                            const displayImage = primaryImage || product.images?.[0];

                                                            return displayImage?.image_url ? (
                                                                <img
                                                                    src={getProductImageUrl(displayImage.image_url)}
                                                                    alt={product.title}
                                                                    className="h-10 w-10 rounded-lg object-cover"
                                                                />
                                                            ) : (
                                                                <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                                                                    <Package className="h-5 w-5 text-gray-400" />
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">{product.title}</div>
                                                        <div className="text-sm text-gray-500">{product.sku}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 font-mono">{product.sku}</td>
                                            {showVendorColumn && (
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    {product.vendor_name || 'Unknown'}
                                                </td>
                                            )}

                                            {showStoreColumn && (
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    {product.store_detail?.name || '—'}
                                                </td>
                                            )}
                                            <td className="px-6 py-4 text-sm text-gray-900">{formatPrice(product.selling_price)}</td>
                                            {showCategoriesColumn && (
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-wrap gap-1">
                                                        {product.category_tags && product.category_tags.length > 0 ? (
                                                            product.category_tags.slice(0, 2).map((tag) => (
                                                                <span
                                                                    key={tag}
                                                                    className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
                                                                >
                                                                    {tag}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="text-sm text-gray-400">—</span>
                                                        )}
                                                        {product.category_tags && product.category_tags.length > 2 && (
                                                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                                                                +{product.category_tags.length - 2}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            <td className="px-6 py-4">
                                                <StockCell product={product} />
                                            </td>

                                            <td className="px-6 py-4">
                                                {product.status === 'changes_requested' && product.rejection_reason ? (
                                                    <div
                                                        ref={triggerRef}
                                                        className="relative inline-block"
                                                        onMouseEnter={(e) => handleMouseEnter(product.id, e)}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>
                                                            <StatusIcon className="h-3 w-3" />
                                                            {status.label}
                                                        </span>

                                                        {activeTooltip === product.id && product.rejection_reason && (
                                                            <div
                                                                className={`absolute z-50 w-72 p-3 bg-white rounded-lg shadow-xl border border-gray-200 ${tooltipPosition === 'top'
                                                                    ? 'bottom-full mb-2'
                                                                    : 'top-full mt-2'
                                                                    }`}
                                                                style={{
                                                                    left: '50%',
                                                                    transform: 'translateX(-50%)',
                                                                    maxWidth: 'calc(100vw - 32px)',
                                                                }}
                                                            >
                                                                {/* Arrow */}
                                                                <div
                                                                    className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-white border ${tooltipPosition === 'top'
                                                                        ? 'bottom-[-5px] border-r border-b'
                                                                        : 'top-[-5px] border-l border-t'
                                                                        } rotate-45`}
                                                                />

                                                                <p className="text-xs font-medium text-gray-700">Reason:</p>
                                                                <p className="text-xs text-gray-600 mt-1 break-words">{product.rejection_reason}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>
                                                        <StatusIcon className="h-3 w-3" />
                                                        {status.label}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-medium">
                                                <div className="flex items-center justify-end gap-1 min-w-[120px]">
                                                    <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                        {/* Submit for Review - shown for draft only */}
                                                        {actions.includes('submit') && product.status === 'draft' && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAction('submit', product, e);
                                                                }}
                                                                className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors"
                                                            >
                                                                Submit
                                                            </button>
                                                        )}
                                                        {/* Resubmit - shown for changes_requested only */}
                                                        {actions.includes('resubmit') && product.status === 'changes_requested' && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAction('resubmit', product, e);
                                                                }}
                                                                className={`px-3 py-1 text-white text-xs font-medium rounded transition-colors ${product.can_resubmit
                                                                    ? 'bg-orange-600 hover:bg-orange-700'
                                                                    : 'bg-gray-400 cursor-not-allowed'
                                                                    }`}
                                                                title={product.can_resubmit ? 'Resubmit for review' : product.resubmit_disabled_reason || 'Make changes before resubmitting'}
                                                            >
                                                                Resubmit
                                                            </button>
                                                        )}
                                                        <div className={`relative mr-6 ${isAdmin && 'mr-12'}`}>
                                                            <button
                                                                onClick={(e) => toggleDropdown(product.id, e)}
                                                                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                                            >
                                                                <MoreVertical className="h-4 w-4" />
                                                            </button>

                                                            {openDropdown === product.id && (
                                                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                                                    {actions.includes('view') && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAction('view', product, e);
                                                                            }}
                                                                            // className="p-1 text-blue-400 hover:text-blue-600 rounded transition-colors"
                                                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                                                            title="View Product"
                                                                        >
                                                                            <Eye className="h-4 w-4" />
                                                                            View
                                                                        </button>
                                                                    )}
                                                                    {/* Review - only for admin and pending_review status */}
                                                                    {actions.includes('review') && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAction('review', product, e);
                                                                            }}
                                                                            disabled={product.status !== 'pending_review'}
                                                                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${product.status === 'pending_review'
                                                                                ? 'text-gray-700 hover:bg-gray-100'
                                                                                : 'text-gray-400'
                                                                                }`}
                                                                            title={product.status === 'pending_review' ? 'Review Product' : 'Product is not pending review'}
                                                                        >
                                                                            <ClipboardCheck className="h-4 w-4" />
                                                                            Review
                                                                        </button>
                                                                    )}
                                                                    {/* Edit - always shown, shows toast if not editable */}
                                                                    {actions.includes('edit') && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAction('edit', product, e);
                                                                            }}
                                                                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${product.can_edit
                                                                                ? 'text-gray-700 hover:bg-gray-100'
                                                                                : 'text-gray-400'
                                                                                }`}
                                                                            title={product.can_edit ? 'Edit Product' : (product.edit_disabled_reason || undefined)}
                                                                        >
                                                                            <Edit className="h-4 w-4" />
                                                                            Edit
                                                                        </button>
                                                                    )}
                                                                    {/* Rollback button - always shown for changes_requested, but may be disabled */}
                                                                    {product.status === 'changes_requested' && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAction('rollback', product, e);
                                                                            }}
                                                                            title={product.can_rollback ? 'Discard Changes' : (product.rollback_disabled_reason || undefined)}
                                                                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${product.can_rollback
                                                                                ? 'text-gray-700 hover:bg-gray-100'
                                                                                : 'text-gray-400'
                                                                                }`}
                                                                        >
                                                                            <Undo2 className="h-4 w-4" />
                                                                            Discard Changes
                                                                        </button>
                                                                    )}
                                                                    {actions.includes('stock') && (
                                                                        <button
                                                                            onClick={(e) => handleAction('stock', product, e)}
                                                                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2`}
                                                                            title="Adjust Stock"
                                                                        >
                                                                            <PackageIcon className="h-4 w-4" />
                                                                            Adjust Stock
                                                                        </button>
                                                                    )}
                                                                    {/* Archive / Restore */}
                                                                    {actions.includes('archive') && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAction(product.is_archived ? 'unarchive' : 'archive', product, e);
                                                                            }}
                                                                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${product.can_archive || product.is_archived
                                                                                ? product.is_archived
                                                                                    ? 'text-green-600 hover:bg-green-50'
                                                                                    : 'text-gray-700 hover:bg-gray-100'
                                                                                : 'text-gray-400'
                                                                                }`}
                                                                            title={!product.can_archive && !product.is_archived ? (product.archive_disabled_reason || undefined) : undefined}
                                                                        >
                                                                            <Archive className="h-4 w-4" />
                                                                            {product.is_archived ? 'Restore' : 'Archive'}
                                                                        </button>
                                                                    )}
                                                                    {/* Delete button - always shown, disabled when not possible */}
                                                                    {actions.includes('delete') && (
                                                                        <button
                                                                            onClick={(e) => handleAction('delete', product, e)}
                                                                            title={product.can_delete ? 'Delete Product' : (product.delete_disabled_reason || undefined)}
                                                                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${product.can_delete
                                                                                ? 'text-red-700 hover:bg-gray-100'
                                                                                : 'text-gray-400'
                                                                                }`}
                                                                        >
                                                                            <Trash2 size={16} />
                                                                            Delete
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            {renderPagination()}
        </div>
    );
};

export default ProductList;